import { DashValidation, FileModel, isNameLengthValid } from 'dash-common';
import {
  Form,
  FormErrorTypes,
  Input,
  Modal,
  useFormState,
  useFormValidation,
} from 'dash-ui';
import { ChangeEvent, FormEvent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import camelCase from 'lodash/camelCase';

import useFormDraftFileUpload from 'common/hooks/useDraftFileUpload';
import {
  GetAmenitiesDocument,
  useCreateAmenityMutation,
  useEditAmenityMutation,
} from 'gql/generated.graphql';
import { ISaveAmenityModal } from './SaveAmenityModal.interfaces';

enum FormFields {
  Name = 'name',
  ImageFileId = 'imageFileId',
}

const FormFieldsList = [FormFields.ImageFileId, FormFields.Name];

export const SaveAmenityModal = ({ modalData, onClose }: ISaveAmenityModal) => {
  const { t } = useTranslation(['pages', 'common', 'forms']);
  const draftFileUpload = useFormDraftFileUpload();
  const formRef = useRef<HTMLFormElement>(null);
  const [formErrors, , [setImageFileIdError, setNameError], [resetFormErrors]] =
    useFormValidation(FormFieldsList);
  const [
    formState,
    [setImageFileId, setName],
    [formOnSubmit, formOnError, , setFormValues],
  ] = useFormState(FormFieldsList);

  const [createAmenity, { loading: createLoading }] = useCreateAmenityMutation({
    refetchQueries: [GetAmenitiesDocument],
  });

  const [editAmenity, { loading: editLoading }] = useEditAmenityMutation({
    refetchQueries: [GetAmenitiesDocument],
  });

  // set initial data state for update
  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    if (modalData?.initialDoc) {
      const { name, imageFile } = modalData?.initialDoc || {};

      setFormValues({ name, imageFileId: imageFile?._id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalData?.initialDoc]);

  const validateName = (name: string) => {
    if (!name.trim()) {
      setNameError({
        type: FormErrorTypes.Invalid,
        message: t('forms:messages.nameBlank'),
      });
      return false;
    }
    if (!isNameLengthValid(name)) {
      setNameError({
        type: FormErrorTypes.Invalid,
        message: t('forms:messages.maxCharacterLimit', {
          number: DashValidation.MaxNameLength.toString(),
        }),
      });
      return false;
    }
    setNameError(null);
    return true;
  };

  const onTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    validateName(e.target.value);
  };

  const validateFile = (imageFileId: string) => {
    if (!imageFileId) {
      setImageFileIdError({
        type: FormErrorTypes.Invalid,
        message: t('forms:messages.iconBlank'),
      });
      return false;
    }
    setImageFileIdError(null);
    return true;
  };

  const onUploadFile = (imageFileId: string) => {
    setImageFileId(imageFileId);
    validateFile(imageFileId);
  };

  const onCloseModal = () => {
    // TODO: figure out why resetForm doesnt work
    // resetForm();
    setFormValues({ name: null, imageFileId: null });
    resetFormErrors();
    onClose();
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    formOnSubmit();
    const { name, imageFileId } = formState;

    if (![validateName(name), validateFile(imageFileId)].every(Boolean)) {
      return null;
    }
    const isCreation = modalData?.mode === 'add';
    const mutation = isCreation ? createAmenity : editAmenity;

    mutation({
      variables: {
        input: {
          _id: modalData?.initialDoc?._id,
          name,
          imageFileId,
        },
      },
      onCompleted: onCloseModal,
      onError: (error) => {
        formOnError(
          t(`common:serverMessages.${error.message}`) || error.message
        );
      },
    });
  };

  const isLoading = createLoading || editLoading;

  return (
    <Modal
      isOpen={!!modalData}
      onRequestClose={onCloseModal}
      title={String(
        modalData?.mode === 'update'
          ? t(`pages:amenityPage.updateAmenityModalLabel`)
          : t('tables:amenities.addNewAmenity')
      )}
      cancelButton={{ title: t('common:labels.cancel'), onClick: onCloseModal }}
      submitButton={{
        title: t(`common:labels.${modalData?.mode}`),
        isLoading,
        disabled: isLoading,
        onClick: () => {
          if (formRef.current) {
            formRef.current.requestSubmit();
          }
        },
      }}
    >
      <Form ref={formRef} onSubmit={onSubmit}>
        <Form.Group mt={2}>
          <Form.Label required>{t('forms:labels.name')}</Form.Label>
          <Input
            w="100%"
            placeholder={t('forms:placeholders.amenityName') || ''}
            onChange={onTextChange}
            value={formState.name || ''}
            $errorType={formErrors[FormFields.Name]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.Name]?.type}>
            {formErrors[FormFields.Name]?.message}
          </Form.Feedback>
        </Form.Group>
        <Form.Group mt={1}>
          <Form.Label required>{t('forms:labels.svgIcon')}</Form.Label>
          <Form.FileUpload.Single
            accept={{ 'image/svg': ['.svg'] }}
            initialFile={modalData?.initialDoc?.imageFile as FileModel}
            maxSize={DashValidation.MaxLogoFileSize}
            onUpload={draftFileUpload}
            onDropRejected={(error) => {
              setImageFileIdError({
                type: FormErrorTypes.Invalid,
                message: t(
                  `forms:messages.uploadErrors.${camelCase(error.code)}`,
                  { type: '.svg' }
                ),
              });
            }}
            onChanged={onUploadFile}
            $errorType={formErrors[FormFields.ImageFileId]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.ImageFileId]?.type}>
            {formErrors[FormFields.ImageFileId]?.message}
          </Form.Feedback>
        </Form.Group>
      </Form>
    </Modal>
  );
};

export default SaveAmenityModal;
